/**
 * PhotoGallery - Centralized photo gallery component
 * 
 * Uses:
 * - PhotoThumbnail for all thumbnails
 * - PhotoLightbox for viewing full images
 * - DeletePhotoDialog for app-styled delete confirmation
 * 
 * This is the SINGLE implementation for photo galleries.
 */

import { useState } from "react";
import { PhotoThumbnail } from "./PhotoThumbnail";
import { PhotoLightbox } from "./PhotoLightbox";
import { DeletePhotoDialog } from "./DeletePhotoDialog";
import { Image as ImageIcon } from "phosphor-react";

export function PhotoGallery({
  photos,
  onDelete,
  onUpdate,
  canEdit = false,
}) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);

  const handleThumbnailClick = (photo) => {
    setSelectedPhoto(photo);
    setLightboxOpen(true);
  };

  const handleEditClick = (photo) => {
    // Open lightbox in edit mode
    setSelectedPhoto(photo);
    setLightboxOpen(true);
  };

  const handleDeleteClick = (photo) => {
    setPhotoToDelete(photo);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = (photoId) => {
    onDelete?.(photoId);
    setPhotoToDelete(null);
  };

  const handleLightboxUpdate = () => {
    onUpdate?.();
  };

  if (!photos?.length) {
    return (
      <div className="text-center py-8 text-zinc-400">
        <ImageIcon size={32} className="mx-auto mb-2" />
        <p>No photos yet</p>
      </div>
    );
  }

  return (
    <>
      {/* Photo grid using PhotoThumbnail */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {photos.map((photo) => (
          <PhotoThumbnail
            key={photo.photo_id}
            photo={photo}
            onClick={handleThumbnailClick}
            onEdit={canEdit ? handleEditClick : undefined}
            onDelete={canEdit ? handleDeleteClick : undefined}
            showVisibilityBadge={true}
            showActions={canEdit}
          />
        ))}
      </div>

      {/* Lightbox for viewing/editing */}
      <PhotoLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        photo={selectedPhoto}
        canEdit={canEdit}
        onUpdate={handleLightboxUpdate}
      />

      {/* Delete confirmation dialog */}
      <DeletePhotoDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        photo={photoToDelete}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </>
  );
}

export default PhotoGallery;
